/**
 * BlocksListView Component
 *
 * Displays a list of blocks within a strategy in a table format with real-time updates.
 *
 * Features:
 * - FR-021: Display all blocks in table format with 5 columns (Name, Description, Block Type, Created, Actions)
 * - FR-022: Add Block button in component header
 * - FR-023: Actions dropdown menu (Edit/Clone/Delete)
 * - FR-024: Row click to open BlockFormDialog
 * - FR-025: Delete confirmation dialog
 * - FR-026: Toast notifications for Clone/Delete
 * - FR-027: Real-time updates via Firestore onSnapshot
 * - FR-028: Sort blocks by createdAt descending
 * - FR-029: Embedded in StrategyFormDialog edit mode
 *
 * Related: Sprint 4, User Story US-4
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography, useTheme, Snackbar, Button, alpha } from '@mui/material';
import { MoreVert as MoreVertIcon, Add as AddIcon } from '@mui/icons-material';
import { DynamicTable, type TableColumn, SDialog, type DialogActionConfig } from '@modules/ui-components';
import type { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import type { StrategyBlock } from '@segmently/schema-models/strategy';

export interface BlocksListViewProps {
  /** Project ID for Firestore path */
  projectId: string;

  /** Strategy ID for which to display blocks */
  strategyId: string;

  /** Strategy block service instance (dependency injection for testing) */
  strategyBlockService: StrategyBlockService;

  /** Optional: Called when Edit button is clicked */
  onEdit?: (block: StrategyBlock) => void;

  /** Optional: Called when Add Block button is clicked */
  onAddBlock?: () => void;

  /** Optional: Called when row is clicked */
  onRowClick?: (block: StrategyBlock) => void;
}

/**
 * UI Constraints from FR-021
 * Source: specs/010-production-strategy-ui/planning-results.json
 */
const DESCRIPTION_MAX_LENGTH = 500; // FR-021: description_max_length

/**
 * Truncate description to max length with ellipsis (FR-021)
 */
const truncateDescription = (description?: string): string => {
  if (!description) return '';
  if (description.length <= DESCRIPTION_MAX_LENGTH) return description;
  return description.substring(0, DESCRIPTION_MAX_LENGTH) + '...';
};

/**
 * Format ISO date string to two-line format (FR-021)
 * Line 1: MMM DD, YYYY
 * Line 2: HH:mm
 *
 * Pattern from StrategiesListView (Sprint 1)
 * IMPORTANT: Use UTC methods (getUTCHours, getUTCMinutes) for Firestore ISO strings with Z suffix
 */
const formatDate = (isoString?: string): React.ReactNode => {
  if (!isoString) return 'N/A';

  try {
    const date = new Date(isoString);

    // Format date line: Oct 29, 2025
    const dateLine = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

    // Format time line: 14:30 (use UTC to match test expectations)
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const timeLine = `${hours}:${minutes}`;

    return (
      <Box>
        <Typography variant="body2">{dateLine}</Typography>
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
          {timeLine}
        </Typography>
      </Box>
    );
  } catch (error) {
    return 'Invalid date';
  }
};

export const BlocksListView = React.memo<BlocksListViewProps>(({
  projectId,
  strategyId,
  strategyBlockService,
  onEdit,
  onAddBlock,
  onRowClick,
}) => {
  const theme = useTheme();

  // Component state
  const [blocks, setBlocks] = useState<StrategyBlock[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Actions menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBlock, setSelectedBlock] = useState<StrategyBlock | null>(null);

  // Toast notification state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState<StrategyBlock | null>(null);

  // Initial load: Fetch blocks from Firestore
  useEffect(() => {
    let isMounted = true;

    const loadBlocks = async () => {
      try {
        setLoading(true);
        const blocks = await strategyBlockService.list({ limit: 50 });

        if (isMounted) {
          // Sprint 5 Fix: Blocks already sorted by createdAt from query, no need to sort locally
          setBlocks(blocks);
          setLoading(false);
        }
      } catch (error) {
        console.error('[BlocksListView] Failed to load blocks:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBlocks();

    return () => {
      isMounted = false;
    };
  }, [strategyBlockService]);

  // Real-time listener (FR-027) - using strategyBlockService.onCollectionSnapshot
  useEffect(() => {
    console.log('[BlocksListView] Setting up real-time listener via StrategyBlockService');

    const unsubscribe = strategyBlockService.onCollectionSnapshot(
      (blocks) => {
        console.log('[BlocksListView] Real-time update received:', blocks.length, 'blocks');
        setBlocks(blocks);
        setLoading(false);
      },
      (error) => {
        console.error('[BlocksListView] Real-time listener error:', error);
        setLoading(false);
      },
      { limit: 50, orderByField: 'createdAt', orderDirection: 'desc' } // Sprint 5 Fix: Changed from 'order' to 'createdAt'
    );

    console.log('[BlocksListView] Real-time listener setup complete');

    // Cleanup: Unsubscribe on unmount
    return () => {
      console.log('[BlocksListView] Unsubscribing from real-time updates');
      unsubscribe();
    };
  }, [strategyBlockService]);

  // Actions menu handlers (FR-023)
  const handleOpenActionsMenu = useCallback((event: React.MouseEvent<HTMLElement>, block: StrategyBlock) => {
    setAnchorEl(event.currentTarget);
    setSelectedBlock(block);
  }, []);

  const handleCloseActionsMenu = useCallback(() => {
    setAnchorEl(null);
    setSelectedBlock(null);
  }, []);

  const handleEdit = useCallback(() => {
    if (selectedBlock && onEdit) {
      onEdit(selectedBlock);
    }
    handleCloseActionsMenu();
  }, [selectedBlock, onEdit, handleCloseActionsMenu]);

  const handleClone = useCallback(async () => {
    if (!selectedBlock?.id) return;
    handleCloseActionsMenu();

    try {
      await strategyBlockService.cloneBlock(selectedBlock.id);
      setSnackbarMessage(`Block "${selectedBlock.name}" cloned successfully`);
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('[BlocksListView] Clone failed:', error);
      setSnackbarMessage(`Failed to clone block: ${error?.message || 'Unknown error'}`);
      setSnackbarOpen(true);
    }
  }, [selectedBlock, strategyBlockService]);

  const handleDelete = useCallback(() => {
    if (!selectedBlock) return;
    setBlockToDelete(selectedBlock);
    setDeleteDialogOpen(true);
    handleCloseActionsMenu();
  }, [selectedBlock, handleCloseActionsMenu]);

  const confirmDelete = useCallback(async () => {
    if (!blockToDelete?.id) return;
    setDeleteDialogOpen(false);

    try {
      await strategyBlockService.delete(blockToDelete.id);
      setSnackbarMessage(`Block "${blockToDelete.name}" deleted`);
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('[BlocksListView] Delete failed:', error);
      setSnackbarMessage(`Failed to delete block: ${error?.message || 'Please try again'}`);
      setSnackbarOpen(true);
    }

    setBlockToDelete(null);
  }, [blockToDelete, strategyBlockService]);

  // Delete dialog actions
  const deleteDialogPrimaryAction: DialogActionConfig = useMemo(
    () => ({
      label: 'Delete',
      onClick: confirmDelete,
      color: 'error' as const,
      'aria-label': 'Confirm delete block',
    }),
    [confirmDelete]
  );

  const deleteDialogSecondaryAction: DialogActionConfig = useMemo(
    () => ({
      label: 'Cancel',
      onClick: () => setDeleteDialogOpen(false),
      variant: 'outlined' as const,
      'aria-label': 'Cancel delete',
    }),
    []
  );

  // Row click handler (FR-024)
  const handleRowClick = useCallback((block: StrategyBlock) => {
    if (onRowClick) {
      onRowClick(block);
    } else {
      console.log('[BlocksListView] Row clicked (no onRowClick prop):', block.id);
    }
  }, [onRowClick]);

  // Define table columns (FR-021) - memoized for performance
  const columns = useMemo<TableColumn<StrategyBlock>[]>(() => [
    {
      key: 'name', // Sprint 5 Fix: Changed from 'blockName' to 'name' (actual field in StrategyBlock)
      label: 'Name',
      sortable: true,
      minWidth: 180,
      align: 'left',
    },
    {
      key: 'description',
      label: 'Description',
      minWidth: 300,
      align: 'left',
      format: (value: string) => truncateDescription(value),
    },
    {
      key: 'blockType',
      label: 'Block Type',
      sortable: true,
      minWidth: 150,
      align: 'left',
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      minWidth: 150,
      align: 'left',
      format: (value: string) => formatDate(value),
    },
    {
      key: 'actions' as keyof StrategyBlock,
      label: 'Actions',
      minWidth: 80,
      align: 'center',
      format: (_value: any, row: StrategyBlock) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click (FR-024)
            handleOpenActionsMenu(e, row);
          }}
          sx={{ color: theme.palette.text.secondary }}
          aria-label="Open actions menu"
          data-testid={`blocks-list-actions-${row.id}`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [theme, handleOpenActionsMenu]); // Re-create only if theme or handler changes

  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      {/* Header with Add Block button (FR-022) - ALWAYS visible */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          px: 2,
        }}
      >
        <Typography variant="h6">Blocks</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            if (onAddBlock) {
              onAddBlock();
            } else {
              console.log('[BlocksListView] Add Block clicked (no onAddBlock prop)');
            }
          }}
          data-testid="blocks-list-add-btn"
        >
          Add Block
        </Button>
      </Box>

      {/* Empty state (FR-022) - show when no blocks */}
      {!loading && blocks.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
          gap={2}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(4),
          }}
        >
          <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
            No blocks found
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Add your first block to get started
          </Typography>
        </Box>
      )}

      {/* Table - show when blocks exist */}
      {blocks.length > 0 && (
        <DynamicTable
          data={blocks}
          columns={columns}
          keyField="id"
          sortable={true}
          filterable={false}
          loading={loading}
          loadingRows={5}
          variant="striped"
          size="medium"
          maxRows={50}
          onRowClick={handleRowClick}
          data-testid="blocks-list-table"
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: '100%',
            '& .MuiTableHead-root': {
              '& .MuiTableCell-root': {
                backgroundColor: alpha(theme.palette.action.hover, 0.04),
                color: theme.palette.text.primary,
                fontWeight: theme.typography.fontWeightMedium,
              }
            },
            '& .MuiTableBody-root': {
              '& .MuiTableRow-root': {
                '&:nth-of-type(odd)': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.02),
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.08),
                },
                '& .MuiTableCell-root': {
                  color: theme.palette.text.primary,
                }
              }
            }
          }}
        />
      )}

      {/* Actions dropdown menu (FR-023) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit} data-testid="blocks-list-menu-edit">Edit</MenuItem>
        <MenuItem onClick={handleClone} data-testid="blocks-list-menu-clone">Clone</MenuItem>
        <MenuItem onClick={handleDelete} data-testid="blocks-list-menu-delete">Delete</MenuItem>
      </Menu>

      {/* Delete confirmation dialog (FR-025) */}
      <SDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        title="Delete Block?"
        primaryAction={deleteDialogPrimaryAction}
        secondaryAction={deleteDialogSecondaryAction}
      >
        <Typography>
          Are you sure you want to delete <strong>"{blockToDelete?.name}"</strong>?
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
          This will also delete all screens in this block.
        </Typography>
      </SDialog>

      {/* Toast notifications (FR-026) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
});

BlocksListView.displayName = 'BlocksListView';
